import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  Chip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import { Product } from "../../pages/manage-products/manageProducts";
import {
  normalizeAmazonUrl,
  isLikelyAmazonUrl,
  AmazonIcon,
} from "../../utils/amazonHelper";

interface AmazonLinkModalProps {
  open: boolean;
  onClose: () => void;
  product: Product | null;
  onSave: (productId: string, amazonLink: string) => Promise<void> | void;
}

const AmazonLinkModal: React.FC<AmazonLinkModalProps> = ({
  open,
  onClose,
  product,
  onSave,
}) => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (product && open) {
      setUrl(product.amazonLink || "");
      setError(null);
      setSuccess(null);
    }
  }, [product, open]);

  if (!product) return null;

  const handlePaste = async () => {
    try {
      if (navigator.clipboard) {
        const text = await navigator.clipboard.readText();
        if (text) {
          setUrl(text.trim());
          setError(null);
        }
      }
    } catch (err) {
      console.error("Failed to read clipboard:", err);
    }
  };

  const handleTestLink = () => {
    if (!url.trim()) return;
    const finalUrl = normalizeAmazonUrl(url);
    window.open(finalUrl, "_blank", "noopener,noreferrer");
  };

  const handleSave = async () => {
    const trimmed = url.trim();
    if (trimmed) {
      try {
        const normalized = normalizeAmazonUrl(trimmed);
        new URL(normalized); // URL syntax check
      } catch (e) {
        setError("Please enter a valid URL (e.g., https://www.amazon.in/dp/...)");
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const finalLink = trimmed ? normalizeAmazonUrl(trimmed) : "";
      await onSave(product._id, finalLink);
      setSuccess(
        finalLink
          ? "Amazon link successfully saved!"
          : "Amazon link removed."
      );
      setTimeout(() => {
        onClose();
      }, 700);
    } catch (err: any) {
      setError(err?.message || "Failed to save Amazon link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    setLoading(true);
    setError(null);
    try {
      await onSave(product._id, "");
      setUrl("");
      setSuccess("Amazon link removed.");
      setTimeout(() => {
        onClose();
      }, 700);
    } catch (err: any) {
      setError(err?.message || "Failed to remove link.");
    } finally {
      setLoading(false);
    }
  };

  const isAmazon = isLikelyAmazonUrl(url);
  const productImage =
    product.images && product.images.length > 0
      ? product.images[0]
      : "https://via.placeholder.com/60x60?text=Item";

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3.5,
          p: 0.5,
          boxShadow: "0 20px 40px -10px rgba(0,0,0,0.15)",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          pt: 2.5,
          px: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.5,
              bgcolor: "#fff7ed",
              border: "1px solid #fed7aa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ea580c",
            }}
          >
            <AmazonIcon size={22} color="#ea580c" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Amazon Link for Order Now
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Direct customers straight to this product on Amazon
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          disabled={loading}
          sx={{ color: "#9ca3af", "&:hover": { color: "#111" } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 1.5 }}>
        {/* Product mini card */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 1.5,
            mb: 2.5,
            bgcolor: "#f9fafb",
            borderRadius: 2.5,
            border: "1px solid #e5e7eb",
          }}
        >
          <Box
            component="img"
            src={productImage}
            alt={product.name}
            sx={{
              width: 50,
              height: 50,
              borderRadius: 2,
              objectFit: "cover",
              bgcolor: "#fff",
              border: "1px solid #e5e7eb",
            }}
            onError={(e: any) => {
              e.currentTarget.src =
                "https://via.placeholder.com/50x50?text=Item";
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {product.name}
            </Typography>
            <Typography variant="body2" sx={{ color: "#2563eb", fontWeight: 700 }}>
              ₹{product.price?.toLocaleString()}
            </Typography>
          </Box>
          {product.amazonLink ? (
            <Chip
              size="small"
              icon={<CheckCircleIcon sx={{ fontSize: "14px !important", color: "#16a34a !important" }} />}
              label="Linked"
              sx={{
                bgcolor: "#f0fdf4",
                color: "#16a34a",
                fontWeight: 600,
                fontSize: "0.72rem",
                border: "1px solid #bbf7d0",
              }}
            />
          ) : (
            <Chip
              size="small"
              label="Not Set"
              sx={{
                bgcolor: "#f3f4f6",
                color: "#6b7280",
                fontWeight: 600,
                fontSize: "0.72rem",
              }}
            />
          )}
        </Box>

        {/* Feedback Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {success}
          </Alert>
        )}

        {/* Amazon URL Input */}
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, mb: 0.75, color: "#374151" }}
        >
          Amazon Product URL:
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="https://www.amazon.in/dp/B0..."
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setError(null);
          }}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LinkIcon sx={{ color: "#f97316" }} />
              </InputAdornment>
            ),
            endAdornment: url ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setUrl("")}
                  edge="end"
                  disabled={loading}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5,
              fontSize: "0.9rem",
              bgcolor: "#fff",
              "&.Mui-focused fieldset": {
                borderColor: "#ea580c",
              },
            },
          }}
        />

        {/* Quick action buttons row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 1.25,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="text"
              startIcon={<ContentPasteIcon sx={{ fontSize: 16 }} />}
              onClick={handlePaste}
              disabled={loading}
              sx={{
                textTransform: "none",
                fontSize: "0.78rem",
                color: "#4b5563",
                py: 0.25,
                px: 1,
              }}
            >
              Paste Link
            </Button>

            {url.trim() && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                onClick={handleTestLink}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontSize: "0.78rem",
                  borderColor: "#fed7aa",
                  color: "#ea580c",
                  py: 0.25,
                  px: 1.2,
                  borderRadius: 2,
                  "&:hover": {
                    bgcolor: "#fff7ed",
                    borderColor: "#f97316",
                  },
                }}
              >
                Test Link
              </Button>
            )}
          </Box>

          {url && isAmazon && (
            <Chip
              size="small"
              label="Valid Amazon Link"
              sx={{
                bgcolor: "#fff7ed",
                color: "#c2410c",
                fontSize: "0.7rem",
                fontWeight: 600,
                border: "1px solid #ffedd5",
              }}
            />
          )}
        </Box>

        {/* Informative helper text */}
        <Box
          sx={{
            mt: 2.5,
            p: 1.5,
            bgcolor: "#fffbeb",
            borderRadius: 2,
            border: "1px solid #fef3c7",
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "#92400e", display: "block", lineHeight: 1.4 }}
          >
            💡 <strong>How it works:</strong> When shoppers click the <strong>Order Now</strong> button on this product in your store or catalog, they will be taken directly to your Amazon listing.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, justifyContent: "space-between" }}>
        {product.amazonLink ? (
          <Button
            variant="text"
            color="error"
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            onClick={handleRemove}
            disabled={loading}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Remove Link
          </Button>
        ) : (
          <Box />
        )}

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={loading}
            sx={{
              textTransform: "none",
              borderRadius: 2.5,
              px: 2.5,
              color: "#4b5563",
              borderColor: "#d1d5db",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ShoppingBagIcon sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              textTransform: "none",
              borderRadius: 2.5,
              px: 3,
              fontWeight: 700,
              bgcolor: "#ea580c",
              color: "#fff",
              boxShadow: "0 2px 8px rgba(234, 88, 12, 0.3)",
              "&:hover": {
                bgcolor: "#c2410c",
              },
            }}
          >
            {loading ? "Saving..." : "Save Amazon Link"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AmazonLinkModal;
